// src/pages/rapim/RapimCreate.tsx
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Save, Calendar, Users,
  FileText, MapPin, Search, CheckCircle, Loader2,
  Upload, X, Paperclip,
} from 'lucide-react';
import { Button, Card, Input, Select, Textarea, Breadcrumb, Badge } from '../../components/ui';
import toast from 'react-hot-toast';
import { adminService } from '../../services/admin';
import { rapimService } from '../../services/rapim';
import { User } from '../../types';

interface PesertaItem {
  id: number;
  name: string;
  role: string;
  selected: boolean;
}

interface LaporanItem {
  id: number;
  kode: string;
  judul: string;
  unit: string;
  risiko: 'rendah' | 'sedang' | 'tinggi' | 'kritis' | 'critical';
  selected: boolean;
  rapim_ulang?: boolean;
  riwayat_ppepp?: {
    ppepp_id?: number;
    progress_terakhir?: number;
    hambatan?: string;
    alasan_kembali?: string;
    tanggal_dikembalikan?: string;
  } | null;
}

const RapimCreate: React.FC = () => {
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'info' | 'peserta' | 'laporan' | 'lampiran'>('info');
  const [pesertaSearch, setPesertaSearch] = useState('');
  const [laporanSearch, setLaporanSearch] = useState('');

  const [formData, setFormData] = useState({
    judul: '',
    tanggal: '',
    waktu: '09:00',
    tempat: '',
    jenis: '' as '' | 'rutin' | 'insidental' | 'darurat',
    agenda: '',
  });

  const [peserta, setPeserta] = useState<PesertaItem[]>([]);
  const [laporan, setLaporan] = useState<LaporanItem[]>([]);
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const [usersRes, laporanRes] = await Promise.all([
          adminService.users.getAll({ per_page: 100 }), // Get all users, potentially need filtered by role if many users
          rapimService.getAvailableLaporan(),
        ]);

        const usersData = (usersRes as any)?.data || usersRes;
        const laporanData = (laporanRes as any)?.data || laporanRes;

        // Transform users to PesertaItem
        const pItems: PesertaItem[] = (Array.isArray(usersData) ? usersData : (usersData?.data || [])).map((u: User) => ({
          id: u.id,
          name: u.name,
          role: u.role?.display_name || u.role?.name || 'User',
          selected: false,
        }));
        setPeserta(pItems);

        // Transform laporan to LaporanItem
        const lItems: LaporanItem[] = (Array.isArray(laporanData) ? laporanData : (laporanData?.data || [])).map((l: any) => ({
          id: l.id,
          kode: l.kode,
          judul: l.judul,
          unit: l.unit?.name || '-',
          risiko: l.tingkat_risiko,
          selected: false,
          rapim_ulang: Boolean(l.rapim_ulang),
          riwayat_ppepp: l.riwayat_ppepp || null,
        }));
        setLaporan(lItems);

      } catch (e) {
        console.error(e);
        toast.error('Gagal memuat data awal');
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  const updateField = (field: string, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => { const n = { ...prev }; delete n[field]; return n; });
    }
  };

  const togglePeserta = (id: number) => {
    setPeserta((prev) => prev.map((p) => p.id === id ? { ...p, selected: !p.selected } : p));
  };

  const toggleLaporan = (id: number) => {
    setLaporan((prev) => prev.map((l) => l.id === id ? { ...l, selected: !l.selected } : l));
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;

    const validFiles: File[] = [];
    const maxSize = 10 * 1024 * 1024; // 10MB

    Array.from(files).forEach((file) => {
      if (file.size > maxSize) {
        toast.error(`File ${file.name} melebihi 10MB`);
        return;
      }
      validFiles.push(file);
    });

    setPendingFiles((prev) => [...prev, ...validFiles]);
    e.target.value = '';
  };

  const removePendingFile = (index: number) => {
    setPendingFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const selectAllPeserta = () => {
    const allSelected = peserta.every((p) => p.selected);
    setPeserta((prev) => prev.map((p) => ({ ...p, selected: !allSelected })));
  };

  const filteredPeserta = peserta.filter((p) =>
    p.name.toLowerCase().includes(pesertaSearch.toLowerCase()) ||
    p.role.toLowerCase().includes(pesertaSearch.toLowerCase())
  );

  const filteredLaporan = laporan.filter((l) =>
    l.judul.toLowerCase().includes(laporanSearch.toLowerCase()) ||
    l.kode.toLowerCase().includes(laporanSearch.toLowerCase())
  );
  const sortedFilteredLaporan = [...filteredLaporan].sort((a, b) => Number(Boolean(b.rapim_ulang)) - Number(Boolean(a.rapim_ulang)));

  const selectedPesertaCount = peserta.filter((p) => p.selected).length;
  const selectedLaporanCount = laporan.filter((l) => l.selected).length;
  const rapimUlangCount = laporan.filter((l) => l.rapim_ulang).length;
  const laporanBaruCount = laporan.length - rapimUlangCount;

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!formData.judul.trim()) newErrors.judul = 'Judul rapat wajib diisi';
    if (!formData.tanggal) newErrors.tanggal = 'Tanggal wajib diisi';
    if (!formData.jenis) newErrors.jenis = 'Jenis rapat wajib dipilih';
    if (selectedPesertaCount === 0) newErrors.peserta = 'Minimal 1 peserta harus dipilih';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) {
      toast.error('Harap lengkapi semua field yang wajib');
      return;
    }
    setSaving(true);
    try {
      const res = await rapimService.create({
        judul: formData.judul,
        tanggal: `${formData.tanggal} ${formData.waktu}:00`,
        tempat: formData.tempat,
        jenis: formData.jenis as any,
        agenda: formData.agenda,
        peserta_ids: peserta.filter(p => p.selected).map(p => p.id),
        laporan_ids: laporan.filter(l => l.selected).map(l => l.id),
      });

      const rapatId = (res as any)?.data?.id || (res as any)?.id;

      if (rapatId && pendingFiles.length > 0) {
        toast.promise(
          Promise.all(pendingFiles.map((file) => rapimService.uploadAttachment(rapatId, file))),
          {
            loading: `Mengunggah ${pendingFiles.length} file...`,
            success: `${pendingFiles.length} file berhasil diunggah`,
            error: 'Beberapa file gagal diunggah',
          }
        );
      }

      toast.success('Rapat berhasil dijadwalkan!');
      navigate('/rapim');
    } catch (e) {
      toast.error('Gagal menyimpan rapat');
      console.error(e);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-teal-main mx-auto mb-3" />
          <p className="text-sm text-neutral-text-secondary">Memuat data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-in">
      <Breadcrumb items={[
        { label: 'Dashboard', href: '/dashboard' },
        { label: 'RAPIM', href: '/rapim' },
        { label: 'Buat Rapat Baru' },
      ]} />

      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-black text-teal-dark tracking-tight">Buat Rapat Pimpinan</h1>
          <p className="text-sm text-neutral-text-secondary mt-1">Jadwalkan rapat baru dan tentukan agenda pembahasan</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 border-b border-neutral-border">
        {[
          { id: 'info' as const, label: 'Informasi Rapat', icon: <Calendar className="w-4 h-4" /> },
          { id: 'peserta' as const, label: `Peserta (${selectedPesertaCount})`, icon: <Users className="w-4 h-4" /> },
          { id: 'laporan' as const, label: `Agenda Laporan (${selectedLaporanCount})`, icon: <FileText className="w-4 h-4" /> },
          { id: 'lampiran' as const, label: `Lampiran (${pendingFiles.length})`, icon: <Paperclip className="w-4 h-4" /> },
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${activeTab === tab.id
              ? 'border-teal-main text-teal-main'
              : 'border-transparent text-neutral-text-secondary hover:text-neutral-text-primary'
              }`}
          >
            {tab.icon} {tab.label}
          </button>
        ))}
      </div>

      {/* Tab: Info */}
      {activeTab === 'info' && (
        <Card>
          <div className="space-y-5 max-w-2xl">
            <Input
              label="Judul Rapat"
              placeholder="Contoh: RAPIM Rutin Minggu ke-5"
              value={formData.judul}
              onChange={(e) => updateField('judul', e.target.value)}
              error={errors.judul}
              required
            />

            <Select
              label="Jenis Rapat"
              options={[
                { value: 'rutin', label: 'Rutin - Rapat terjadwal berkala' },
                { value: 'insidental', label: 'Insidental - Rapat khusus' },
                { value: 'darurat', label: 'Darurat - Rapat mendesak' },
              ]}
              placeholder="Pilih jenis rapat"
              value={formData.jenis}
              onChange={(v) => updateField('jenis', v)}
              error={errors.jenis}
              required
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Tanggal"
                type="date"
                value={formData.tanggal}
                onChange={(e) => updateField('tanggal', e.target.value)}
                error={errors.tanggal}
                required
                min={new Date().toISOString().split('T')[0]}
              />
              <Input
                label="Waktu"
                type="time"
                value={formData.waktu}
                onChange={(e) => updateField('waktu', e.target.value)}
              />
            </div>

            <Input
              label="Tempat"
              placeholder="Contoh: Ruang Rapat Rektor Lt.3 / Zoom Meeting"
              value={formData.tempat}
              onChange={(e) => updateField('tempat', e.target.value)}
              icon={<MapPin className="w-4 h-4" />}
            />

            <Textarea
              label="Agenda"
              placeholder="Tuliskan agenda rapat (satu per baris)..."
              value={formData.agenda}
              onChange={(e) => updateField('agenda', e.target.value)}
              rows={6}
              helpText="Gunakan format daftar untuk memudahkan pembacaan"
            />
          </div>
        </Card>
      )}

      {/* Tab: Peserta */}
      {activeTab === 'peserta' && (
        <Card>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <p className="text-sm text-neutral-text-secondary">
                {selectedPesertaCount} peserta dipilih
              </p>
              <Button variant="ghost" size="sm" onClick={selectAllPeserta}>
                {peserta.every((p) => p.selected) ? 'Batal Semua' : 'Pilih Semua'}
              </Button>
            </div>

            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-text-secondary" />
              <input
                type="text"
                placeholder="Cari peserta..."
                value={pesertaSearch}
                onChange={(e) => setPesertaSearch(e.target.value)}
                className="w-full pl-9 pr-4 py-2 rounded-lg border border-neutral-border text-sm focus:outline-none focus:ring-2 focus:ring-teal-light"
              />
            </div>

            {errors.peserta && (
              <p className="text-xs text-danger">{errors.peserta}</p>
            )}

            <div className="space-y-2 max-h-96 overflow-y-auto">
              {filteredPeserta.map((p) => (
                <label
                  key={p.id}
                  className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-all ${p.selected ? 'bg-teal-main/5 border border-teal-main/20' : 'bg-neutral-bg hover:bg-gray-100'
                    }`}
                >
                  <input
                    type="checkbox"
                    checked={p.selected}
                    onChange={() => togglePeserta(p.id)}
                    className="rounded border-neutral-border text-teal-main focus:ring-teal-light"
                  />
                  <div className="w-8 h-8 rounded-full bg-teal-main text-white flex items-center justify-center text-xs font-bold">
                    {p.name.charAt(0)}
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium">{p.name}</p>
                    <p className="text-xs text-neutral-text-secondary">{p.role}</p>
                  </div>
                  {p.selected && <CheckCircle className="w-4 h-4 text-teal-main" />}
                </label>
              ))}
            </div>
          </div>
        </Card>
      )}

      {/* Tab: Laporan */}
      {activeTab === 'laporan' && (
        <Card>
          <div className="space-y-4">
            <div className="flex items-start gap-2 p-3 bg-info/5 border border-info/20 rounded-xl">
              <FileText className="w-5 h-5 text-info shrink-0 mt-0.5" />
              <p className="text-sm text-info">
                Pilih laporan yang sudah tervalidasi Level 2 untuk dibahas dalam rapat.
                Anda juga bisa menambahkan laporan setelah rapat dibuat.
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2 text-xs">
              <span className="inline-flex items-center rounded-full bg-amber-100 text-amber-800 px-2.5 py-1 font-semibold">
                RAPIM Ulang: {rapimUlangCount}
              </span>
              <span className="inline-flex items-center rounded-full bg-slate-100 text-slate-700 px-2.5 py-1 font-semibold">
                Laporan Baru: {laporanBaruCount}
              </span>
            </div>
            {rapimUlangCount > 0 && (
              <div className="rounded-xl border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-800">
                Ada {rapimUlangCount} laporan dikembalikan ke RAPIM. Item ini ditampilkan lebih atas agar mudah dibedakan dari laporan baru.
              </div>
            )}

            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-text-secondary" />
              <input
                type="text"
                placeholder="Cari laporan (kode / judul)..."
                value={laporanSearch}
                onChange={(e) => setLaporanSearch(e.target.value)}
                className="w-full pl-9 pr-4 py-2 rounded-lg border border-neutral-border text-sm focus:outline-none focus:ring-2 focus:ring-teal-light"
              />
            </div>

            <div className="space-y-2">
              {sortedFilteredLaporan.map((l) => (
                <label
                  key={l.id}
                  className={`flex items-center gap-3 p-4 rounded-xl cursor-pointer transition-all ${l.selected
                    ? 'bg-teal-main/5 border-2 border-teal-main/30'
                    : l.rapim_ulang
                      ? 'bg-amber-50 border-2 border-amber-200 hover:border-amber-300'
                      : 'bg-neutral-bg border-2 border-transparent hover:border-neutral-border'
                    }`}
                >
                  <input
                    type="checkbox"
                    checked={l.selected}
                    onChange={() => toggleLaporan(l.id)}
                    className="rounded border-neutral-border text-teal-main focus:ring-teal-light"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs font-mono text-teal-main">{l.kode}</span>
                      {l.rapim_ulang && (
                        <span className="inline-flex items-center rounded-full bg-amber-100 text-amber-800 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide">
                          RAPIM Ulang
                        </span>
                      )}
                      <Badge variant="risk" risk={l.risiko === 'critical' ? 'kritis' : l.risiko as any} />
                    </div>
                    <p className="text-sm font-medium truncate">{l.judul}</p>
                    <p className="text-xs text-neutral-text-secondary mt-0.5">{l.unit}</p>
                    {l.rapim_ulang && (
                      <div className="mt-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-2 py-1.5">
                        <div className="font-semibold">⚠️ RAPIM Ulang — Pernah dikembalikan dari tindak lanjut</div>
                        <div className="mt-0.5">
                          Progres terakhir: {l.riwayat_ppepp?.progress_terakhir ?? 0}% — Alasan: {l.riwayat_ppepp?.alasan_kembali || '-'}
                        </div>
                        {l.riwayat_ppepp?.ppepp_id && (
                          <div className="mt-0.5">
                            <Link to={`/tindak-lanjut/${l.riwayat_ppepp.ppepp_id}`} className="text-amber-800 underline">
                              Lihat Riwayat PPEPP Sebelumnya
                            </Link>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  {l.selected && <CheckCircle className="w-5 h-5 text-teal-main shrink-0" />}
                </label>
              ))}

              {filteredLaporan.length === 0 && (
                <div className="text-center py-8 text-sm text-neutral-text-secondary">
                  <FileText className="w-10 h-10 mx-auto mb-2 text-neutral-border" />
                  <p>Tidak ada laporan yang tersedia</p>
                </div>
              )}
            </div>
          </div>
        </Card>
      )}

      {/* Tab: Lampiran */}
      {activeTab === 'lampiran' && (
        <Card>
          <div className="space-y-4">
            <div className="flex items-start gap-2 p-3 bg-info/5 border border-info/20 rounded-xl">
              <Paperclip className="w-5 h-5 text-info shrink-0 mt-0.5" />
              <p className="text-sm text-info">
                Upload dokumen pendukung rapat (maks. 10MB per file). File akan diunggah setelah rapat berhasil dibuat.
              </p>
            </div>

            <div className="border-2 border-dashed border-neutral-border rounded-xl p-8 text-center">
              <input
                type="file"
                id="file-upload"
                multiple
                onChange={handleFileSelect}
                className="hidden"
                accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.jpg,.jpeg,.png,.zip"
              />
              <label
                htmlFor="file-upload"
                className="cursor-pointer flex flex-col items-center"
              >
                <Upload className="w-10 h-10 text-neutral-text-secondary mb-3" />
                <p className="text-sm font-medium text-neutral-text-primary mb-1">
                  Klik untuk memilih file atau drag & drop
                </p>
                <p className="text-xs text-neutral-text-secondary">
                  PDF, DOC, XLS, PPT, JPG, PNG, ZIP (maks. 10MB)
                </p>
              </label>
            </div>

            {pendingFiles.length > 0 && (
              <div className="space-y-2">
                <p className="text-sm font-medium text-neutral-text-secondary">
                  File yang akan diunggah:
                </p>
                {pendingFiles.map((file, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between p-3 bg-neutral-bg rounded-lg"
                  >
                    <div className="flex items-center gap-3">
                      <Paperclip className="w-4 h-4 text-neutral-text-secondary" />
                      <div>
                        <p className="text-sm font-medium">{file.name}</p>
                        <p className="text-xs text-neutral-text-secondary">
                          {formatFileSize(file.size)}
                        </p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => removePendingFile(index)}
                      className="p-1.5 hover:bg-red-100 rounded-lg transition-colors"
                    >
                      <X className="w-4 h-4 text-red-500" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </Card>
      )}

      {/* Footer Actions */}
      <div className="flex items-center justify-between">
        <Button
          variant="outline"
          onClick={() => navigate('/rapim')}
          icon={<ArrowLeft className="w-4 h-4" />}
        >
          Batal
        </Button>
        <Button
          variant="primary"
          onClick={handleSave}
          loading={saving}
          icon={<Save className="w-4 h-4" />}
        >
          Simpan & Jadwalkan
        </Button>
      </div>
    </div>
  );
};

export default RapimCreate;
